// ---------------------------------------------------------------------------
// Yield: lots produce it, extractors multiply it
// ---------------------------------------------------------------------------
//
// A lot is a Hive cell whose resourceType names a lot type (timber, metal…).
// Same-type lots joined through shared hex edges form a network. Each lot
// yields yieldPerLotPerRound a round; a network that a standing extractor of
// its type sits on yields extractorYieldMultiplier times that. Structures
// produce nothing themselves: an extractor is purely a multiplier.
//
//   - A destroyed lot cell produces nothing and splits its network, so lots
//     cut off from the extractor drop to the base amount.
//   - An extractor keeps multiplying until every one of its cells is
//     destroyed (partial damage doesn't dent it).
//   - A lot under a structure whose stopsLotProduction is set is out of its
//     network.
//   - Broods are not structures and touch none of this.
//
// A war structure carries what the yield needs, snapshotted from the player's
// Hive when the war started (progression/warSetup.js): extractorFor (a lot
// type or null) and stopsLotProduction. A structure without them (a Hive from
// before rules v8) is inert.
//
// Yield is credited after the round resolves (resolveRound), so a lot or
// extractor destroyed this round doesn't pay this round. The tick's
// { yieldIncrement } shape is the stable interface; byType says which lot
// types produced it (war end converts leftover yield in those proportions).

import { rules } from './config.js'
import { hexNeighbors } from './hexGrid.js'
import { isStructureDestroyed } from './subplotGrid.js'
import { sumHook } from './structureEffects.js'

// Same-type lot networks: [{ type, cells }], each joined through shared edges.
// `lots` maps cell → lot type; `blocked` cells are left out (and so split
// networks).
export function lotNetworks(lots, blocked = new Set()) {
    const seen = new Set()
    const nets = []
    for (const key of Object.keys(lots)) {
        const start = Number(key)
        if (seen.has(start) || blocked.has(start)) continue
        const type = lots[start]
        const cells = []
        const stack = [start]
        seen.add(start)
        while (stack.length) {
            const c = stack.pop()
            cells.push(c)
            for (const n of hexNeighbors(c)) {
                if (!seen.has(n) && !blocked.has(n) && lots[n] === type) { seen.add(n); stack.push(n) }
            }
        }
        nets.push({ type, cells: cells.sort((a, b) => a - b) })
    }
    return nets
}

// One round's lot yield.
//   lots        { [cell]: lotType }
//   structures  [{ footprint, extractorFor, stopsLotProduction }]
//   destroyed   Set of destroyed cells
// Returns { total, byType } (whole numbers, floored per type).
export function lotYield({ lots, structures, destroyed = new Set() }, { perLot, multiplier }) {
    const blocked = new Set(destroyed)
    const extractors = []
    for (const st of structures) {
        if (st.stopsLotProduction) for (const c of st.footprint) blocked.add(c)
        const live = st.footprint.filter(c => !destroyed.has(c))
        if (st.extractorFor && live.length) extractors.push({ type: st.extractorFor, cells: live })
    }
    const byType = {}
    for (const net of lotNetworks(lots, blocked)) {
        const inNet = new Set(net.cells)
        const boosted = extractors.some(x => x.type === net.type && x.cells.some(c => inNet.has(c)))
        byType[net.type] = (byType[net.type] ?? 0) + net.cells.length * perLot * (boosted ? multiplier : 1)
    }
    for (const t of Object.keys(byType)) byType[t] = Math.floor(byType[t])
    return { total: Object.values(byType).reduce((a, n) => a + n, 0), byType }
}

// A war Hive's lots and structures, in lotYield's terms.
export function hiveLots(homefront) {
    const lots = {}
    const destroyed = new Set()
    homefront.subplots.forEach((cell, s) => {
        if (cell.resourceType) lots[s] = cell.resourceType
        if (cell.destroyed) destroyed.add(s)
    })
    const structures = Object.values(homefront.structures ?? {}).map(st => ({
        footprint: st.footprint, extractorFor: st.extractorFor ?? null, stopsLotProduction: !!st.stopsLotProduction,
    }))
    return { lots, structures, destroyed }
}

// Per-round yield off a war Hive: its lots, plus any structure effect's
// roundYield (structureEffects.js). Returns { yieldIncrement, byType };
// byType covers the lot yield only.
export function tickYield(homefront) {
    if (!homefront?.subplots) return { yieldIncrement: 0, byType: {} }
    const { total, byType } = lotYield(hiveLots(homefront), { perLot: rules().yieldPerLotPerRound, multiplier: rules().extractorYieldMultiplier })
    const bonus = sumHook('roundYield', homefront, st => !isStructureDestroyed(homefront, st.id))
    return { yieldIncrement: total + Math.floor(bonus), byType }
}
